import { Box, IconButton, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../assets/errors/nodata.png';

const Borrow = () => {
  const [borrows, setBorrows] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBorrow, setSelectedBorrow] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openStaffDialog, setOpenStaffDialog] = useState(false);
  const [openEquipmentDialog, setOpenEquipmentDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [borrowToDelete, setBorrowToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  const [formData, setFormData] = useState({
    staff_id: '',
    date: '',
    equipment: '',
    duration: '',
    type: 'daily',
    destination: '',
    description: '',
    sign_out: '',
    s_out_verified_by: '',
    sign_in: '',
    s_in_verified_by: '',
    coment: '',
    status: '0',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [borrowsResponse, usersResponse, inventoryResponse] = await Promise.all([
          getData('proccess.php', 'borrow_V2'),
          getData('proccess.php', 'staffs'),
          getData('proccess.php', 'inventory')
        ]);

        if (borrowsResponse.success) {
          setBorrows(borrowsResponse.data);
        } else {
          toast.error(borrowsResponse.message || "Failed to load borrows");
        }

        if (usersResponse.success) {
          setStaffs(usersResponse.data);
        } else {
          toast.error(usersResponse.message || "Failed to load staff");
        }

        if (inventoryResponse.success) {
          const inventoryData = inventoryResponse.data.filter(item => item.status === '0' && item.categotyType === 'laptop');
          setInventory(inventoryData);
        } else {
          toast.error(inventoryResponse.message || "Failed to load inventory");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  // Function to export data to Excel
  const exportToExcel = () => {
    if (borrows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Map status codes to readable text
      const statusMap = {
        '0': 'Pending',
        '1': 'In Progress',
        '2': 'Approved',
        '3': 'Rejected',
        '4': 'Returned'
      };

      // Map borrow types to readable text
      const typeMap = {
        'Temporary': 'Temporary',
        'Permanent': 'Permanent'
      };

      // Prepare data for export
      const exportData = borrows.map(borrow => {
        const staff = staffs.find(s => s.Id === borrow.staff_id);
        const equipment = inventory.find(i => i.id === borrow.equipment);
        const signOutVerifier = staffs.find(s => s.Id === borrow.s_out_verified_by);
        const signInVerifier = staffs.find(s => s.Id === borrow.s_in_verified_by);

        return {
          'ID': borrow.id || '',
          'Staff Name': staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown',
          'Borrow Date': borrow.date || '',
          'Equipment': equipment ? equipment.name : 'Unknown',
          'Duration': borrow.duration || '',
          'Borrow Type': typeMap[borrow.type] || borrow.type || '',
          'Destination': borrow.destination || '',
          'Description': borrow.description || '',
          'Sign Out': borrow.sign_out || '',
          'Sign Out Verified By': signOutVerifier ? `${signOutVerifier.FirstName} ${signOutVerifier.LastName}` : '',
          'Sign In': borrow.sign_in || '',
          'Sign In Verified By': signInVerifier ? `${signInVerifier.FirstName} ${signInVerifier.LastName}` : '',
          'Comment': borrow.coment || '',
          'Status': statusMap[borrow.status] || 'Unknown'
        };
      });

      // Create CSV content
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `borrows_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Borrow data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export borrow data");
    }
  };

  // Alternative function using XLSX library (if you prefer proper Excel format)
  // You'll need to install: npm install xlsx
  /*
  const exportToExcelAdvanced = async () => {
    if (borrows.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const XLSX = await import('xlsx');
      
      // Prepare data (same as above)
      const statusMap = {
        '0': 'Pending',
        '1': 'In Progress',
        '2': 'Approved',
        '3': 'Rejected',
        '4': 'Returned'
      };

      const typeMap = {
        'Temporary': 'Temporary',
        'Permanent': 'Permanent'
      };

      const exportData = borrows.map(borrow => {
        const staff = staffs.find(s => s.Id === borrow.staff_id);
        const equipment = inventory.find(i => i.id === borrow.equipment);
        const signOutVerifier = staffs.find(s => s.Id === borrow.s_out_verified_by);
        const signInVerifier = staffs.find(s => s.Id === borrow.s_in_verified_by);

        return {
          'ID': borrow.id || '',
          'Staff Name': staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown',
          'Borrow Date': borrow.date || '',
          'Equipment': equipment ? equipment.name : 'Unknown',
          'Duration': borrow.duration || '',
          'Borrow Type': typeMap[borrow.type] || borrow.type || '',
          'Destination': borrow.destination || '',
          'Description': borrow.description || '',
          'Sign Out': borrow.sign_out || '',
          'Sign Out Verified By': signOutVerifier ? `${signOutVerifier.FirstName} ${signOutVerifier.LastName}` : '',
          'Sign In': borrow.sign_in || '',
          'Sign In Verified By': signInVerifier ? `${signInVerifier.FirstName} ${signInVerifier.LastName}` : '',
          'Comment': borrow.coment || '',
          'Status': statusMap[borrow.status] || 'Unknown'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Borrows Data');
      
      // Generate Excel file and download
      XLSX.writeFile(wb, `borrows_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success("Borrow data exported successfully as Excel file");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export borrow data");
    }
  };
  */

  const handleAddClick = () => {
    setFormData({
      staff_id: '',
      date: '',
      equipment: '',
      duration: 'Working Hours',
      type: 'Daily',
      destination: 'Mindsinsction STEAM Centre',
      description: '',
      sign_out: '',
      s_out_verified_by: '',
      sign_in: '',
      s_in_verified_by: '',
      coment: '',
      status: '0',
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (borrow) => {
    setSelectedBorrow(borrow);
    setFormData({
      staff_id: borrow.staff_id || '',
      date: borrow.date || '',
      equipment: borrow.equipment || '',
      duration: borrow.duration || '',
      type: borrow.type || '',
      destination: borrow.destination || '',
      description: borrow.description || '',
      sign_out: borrow.sign_out || '',
      s_out_verified_by: borrow.s_out_verified_by || '',
      sign_in: borrow.sign_in || '',
      s_in_verified_by: borrow.s_in_verified_by || '',
      coment: borrow.coment || '',
      status: borrow.status || '0',
    });
    setOpenEditDialog(true);
  };

  const handleStaffClick = (staffId) => {
    const staff = staffs.find(s => s.Id === staffId);
    setSelectedStaff(staff || null);
    setOpenStaffDialog(true);
  };

  const handleEquipmentClick = (equipmentId) => {
    const equipment = inventory.find(i => i.id === equipmentId);
    setSelectedEquipment(equipment || null);
    setOpenEquipmentDialog(true);
  };

  const handleHrApproveClick = async (borrow) => {
    try {
      const response = await updateData('proccess.php', 'borrow_V2', {
        updType: 'hr',
        status: 1,
        s_out_verified_by: userID,
        sign_out: new Date().toISOString().slice(0, 16),
      }, borrow.id);
      
      if (response.success) {
        toast.success(`Borrow approved by ${userRole} successfully`);
        setBorrows(prev => prev.map(b => b.id === borrow.id ? { ...b, status: '1', s_out_verified_by: userID, sign_out: new Date().toISOString().slice(0, 16) } : b));
      } else {
        toast.error(response.message || "Failed to approve borrow");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve borrow");
      console.error("Approve error:", err);
    }
  };

  const handleSupervisorApproveClick = async (borrow) => {
    try {
      const response = await updateData('proccess.php', 'borrow_V2', {
        updType: 'spvsr',
        status: 2,
        s_in_verified_by: userID,
        sign_in: new Date().toISOString().slice(0, 16),
      }, borrow.id);
      
      if (response.success) {
        toast.success("Borrow approved by supervisor successfully");
        setBorrows(prev => prev.map(b => b.id === borrow.id ? { ...b, status: '2', s_in_verified_by: userID, sign_in: new Date().toISOString().slice(0, 16) } : b));
      } else {
        toast.error(response.message || "Failed to approve borrow");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve borrow");
      console.error("Approve error:", err);
    }
  };

  const handleApproveClick = async (borrow) => {
    try {
      const response = await updateData('proccess.php', 'borrow_V2', {
        updType: 'appr',
        status: 4,
        s_in_verified_by: userID,
        sign_in: new Date().toISOString().slice(0, 16),
      }, borrow.id);
      
      if (response.success) {
        toast.success("Borrow fully approved successfully");
        setBorrows(prev => prev.map(b => b.id === borrow.id ? { ...b, status: '4', s_in_verified_by: userID, sign_in: new Date().toISOString().slice(0, 16) } : b));
      } else {
        toast.error(response.message || "Failed to approve borrow");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve borrow");
      console.error("Approve error:", err);
    }
  };

  const handleRejectClick = async (borrow) => {
    try {
      const response = await updateData('proccess.php', 'borrow_V2', {
        updType: 'reject',
        status: 3,
        rejected_by: userID,
        rejected_date: new Date().toISOString().split('T')[0],
      }, borrow.id);
      
      if (response.success) {
        toast.success("Borrow rejected successfully");
        setBorrows(prev => prev.map(b => b.id === borrow.id ? { ...b, status: '3', rejected_by: userID, rejected_date: new Date().toISOString().split('T')[0] } : b));
      } else {
        toast.error(response.message || "Failed to reject borrow");
      }
    } catch (err) {
      toast.error("Network error: Failed to reject borrow");
      console.error("Reject error:", err);
    }
  };

  const handleDeleteClick = (borrow) => {
    setBorrowToDelete(borrow);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData('proccess.php', 'borrow_V2', borrowToDelete.id);
      if (response.success) {
        toast.success("Borrow deleted successfully");
        setBorrows(borrows.filter(b => b.id !== borrowToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete borrow");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete borrow");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setBorrowToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedBorrow) {
        response = await updateData('proccess.php', 'borrow_V2', formData, selectedBorrow.id);
        if (response.success) {
          toast.success("Borrow updated successfully");
          setBorrows(prev => prev.map(b => b.id === selectedBorrow.id ? { ...b, ...formData } : b));
        }
      } else {
        response = await addData('proccess.php', 'borrow_V2', formData);
        if (response.success) {
          toast.success("Borrow added successfully");
          setBorrows(prev => [...prev, { ...formData, id: response.data.id }]);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedBorrow(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
    {
      field: "staff_id",
      headerName: "Full Name",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.staff_id);
        return (
          <Box width="100%" display="flex" alignItems="center" justifyContent={'center'} height={'100%'}>
            <Typography
              sx={{ cursor: 'pointer', color: colors.blue[400] }}
              onClick={() => staff && handleStaffClick(params.row.staff_id)}
            >
              {staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown'}
            </Typography>
          </Box>
        );
      }
    },
    { field: "date", headerName: "Borrow Date", flex: 1, minWidth: 120 },
    {
      field: "equipment",
      headerName: "Equipment",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const equipment = inventory.find(i => i.id === params.row?.equipment);
        return (
          <Box width="100%" display="flex" alignItems="center" justifyContent={'center'} height={'100%'}>
            <Typography
              sx={{ cursor: equipment ? 'pointer' : 'default', color: equipment ? colors.blue[400] : 'inherit' }}
              onClick={() => equipment && handleEquipmentClick(params.row.equipment)}
            >
              {equipment ? equipment.name : 'Unknown'}
            </Typography>
          </Box>
        );
      }
    },
    { field: "duration", headerName: "Duration", flex: 1, minWidth: 120 },
    { field: "type", headerName: "Borrow Type", flex: 1, minWidth: 120 },
    { field: "destination", headerName: "Destination", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    { field: "sign_out", headerName: "Sign Out", flex: 1, minWidth: 120 },
    {
      field: "s_out_verified_by",
      headerName: "Sign Out Verified By",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.s_out_verified_by);
        return (
          <Box width="100%" display="flex" alignItems="center" justifyContent={'center'} height={'100%'}>
            <Typography
              sx={{ cursor: staff ? 'pointer' : 'default', color: staff ? colors.blue[400] : 'inherit' }}
              onClick={() => staff && handleStaffClick(params.row.s_out_verified_by)}
            >
              {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            </Typography>
            {(userRole === 'admin' || userRole === 'hr') && params.row?.status === 0 && (
              <IconButton
                onClick={() => handleHrApproveClick(params.row)}
                sx={{ color: colors.blue[400] }}
                disabled={params.row?.status !== 0}
              >
                <CheckOutlinedIcon />
              </IconButton>
            )}
          </Box>
        );
      }
    },
    { field: "sign_in", headerName: "Sign In", flex: 1, minWidth: 120 },
    {
      field: "s_in_verified_by",
      headerName: "Sign In Verified By",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.s_in_verified_by);
        return (
          <Box width="100%" display="flex" alignItems="center" justifyContent={'center'} height={'100%'}>
            <Typography
              sx={{ cursor: staff ? 'pointer' : 'default', color: staff ? colors.blue[400] : 'inherit' }}
              onClick={() => staff && handleStaffClick(params.row.s_in_verified_by)}
            >
              {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            </Typography>
            {(userRole === 'admin' || userRole === 'hr') && params.row?.status === 1 && (
              <IconButton
                onClick={() => handleSupervisorApproveClick(params.row)}
                sx={{ color: colors.blue[400] }}
                disabled={params.row?.status !== 1}
              >
                <CheckOutlinedIcon />
              </IconButton>
            )}
          </Box>
        );
      }
    },
    { field: "coment", headerName: "Comment", flex: 1, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const statusText = {
          0: 'Pending',
          1: 'In Progress',
          2: 'Approved',
          3: 'Rejected',
          4: 'Returned'
        };
        const statusColor = {
          0: colors.orange[100],
          1: colors.orange[100],
          2: colors.blue[100],
          3: colors.red[100],
          4: colors.blue[100],
        };
        return (
          <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h5" fontWeight="bold" color={statusColor[params.row?.status]}>
              {statusText[params.row?.status] || 'Unknown'}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleApproveClick(params.row)}
            sx={{ color: colors.blue[400] }}
            disabled={(params.row?.status === '4' || params.row?.status < '2') && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <CheckOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleRejectClick(params.row)}
            sx={{ color: colors.red[400] }}
            disabled={(params.row?.status === '1' || params.row?.status > '2') && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <ClearOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.red[800] }}
            disabled={(params.row?.status === '1' || params.row?.status > '1') && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m="10px">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Staff Details Dialog */}
      <Dialog
        open={openStaffDialog}
        onClose={() => setOpenStaffDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Staff Details</DialogTitle>
        <DialogContent>
          {selectedStaff ? (
            <Box>
              <Typography><strong>Name:</strong> {selectedStaff.FirstName} {selectedStaff.LastName}</Typography>
              <Typography><strong>Email:</strong> {selectedStaff.email}</Typography>
              <Typography><strong>Cell:</strong> {selectedStaff.Cell || 'N/A'}</Typography>
              <Typography><strong>Role:</strong> {selectedStaff.role}</Typography>
              <Typography><strong>Status:</strong> {selectedStaff.status}</Typography>
              <Typography><strong>Verified:</strong> {selectedStaff.isVerified === '1' ? 'Yes' : 'No'}</Typography>
              <Typography><strong>Registration Date:</strong> {selectedStaff.createDate}</Typography>
              <Typography><strong>Last Login:</strong> {selectedStaff.last_login || 'N/A'}</Typography>
            </Box>
          ) : (
            <Typography>No staff details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStaffDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Equipment Details Dialog */}
      <Dialog
        open={openEquipmentDialog}
        onClose={() => setOpenEquipmentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Equipment Details</DialogTitle>
        <DialogContent>
          {selectedEquipment ? (
            <Box>
              <Typography><strong>Name:</strong> {selectedEquipment.description}</Typography>
              <Typography><strong>SKU:</strong> {selectedEquipment.sku}</Typography>
              <Typography><strong>Category:</strong> {selectedEquipment.category}</Typography>
              <Typography><strong>Type:</strong> {selectedEquipment.categotyType}</Typography>
              <Typography><strong>Location:</strong> {selectedEquipment.location}</Typography>
              <Typography><strong>Serial:</strong> {selectedEquipment.serialNumber || 'N/A'}</Typography>
              <Typography><strong>Condition:</strong> {selectedEquipment.product_condition}</Typography>
              <Typography><strong>Comment:</strong> {selectedEquipment.comment || 'N/A'}</Typography>
              <Typography><strong>Status:</strong> {selectedEquipment.status}</Typography>
            </Box>
          ) : (
            <Typography>No equipment details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEquipmentDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedBorrow(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedBorrow ? "Edit Borrow" : "Add New Borrow"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display="flex" gap="1rem" width="100%" mb="1rem" flexWrap="wrap">
              <TextField
                select
                style={{ width: "100%" }}
                id="staff_id"
                name="staff_id"
                label="Staff Member"
                variant="standard"
                value={formData.staff_id}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                style={{ width: "48%" }}
                id="date"
                name="date"
                label="Borrow Date"
                type="date"
                variant="standard"
                value={formData.date}
                onChange={handleFormChange}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                style={{ width: "48%" }}
                id="equipment"
                name="equipment"
                label="Equipment"
                variant="standard"
                value={formData.equipment}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {inventory.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.description}
                  </option>
                ))}
              </TextField>

              <TextField
                style={{ width: "48%" }}
                id="duration"
                name="duration"
                label="Duration"
                variant="standard"
                value={formData.duration}
                onChange={handleFormChange}
                required
              />

              <TextField
                select
                style={{ width: "48%" }}
                id="type"
                name="type"
                label="Borrow Type"
                variant="standard"
                value={formData.type}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                <option value="Temporary">Temporary</option>
                <option value="Permanent">Permanent</option>
              </TextField>

              <TextField
                style={{ width: "100%" }}
                id="destination"
                name="destination"
                label="Destination"
                variant="standard"
                value={formData.destination}
                onChange={handleFormChange}
                required
              />

              <TextField
                style={{ width: "100%" }}
                id="description"
                name="description"
                label="Description"
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={2}
              />

              <TextField
                style={{ width: "48%" }}
                id="sign_out"
                name="sign_out"
                label="Sign Out"
                type="datetime-local"
                variant="standard"
                value={formData.sign_out}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                style={{ width: "48%" }}
                id="s_out_verified_by"
                name="s_out_verified_by"
                label="Sign Out Verified By"
                variant="standard"
                value={formData.s_out_verified_by}
                onChange={handleFormChange}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                style={{ width: "48%" }}
                id="sign_in"
                name="sign_in"
                label="Sign In"
                type="datetime-local"
                variant="standard"
                value={formData.sign_in}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                style={{ width: "48%" }}
                id="s_in_verified_by"
                name="s_in_verified_by"
                label="Sign In Verified By"
                variant="standard"
                value={formData.s_in_verified_by}
                onChange={handleFormChange}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                style={{ width: "100%" }}
                id="coment"
                name="coment"
                label="Comment"
                variant="standard"
                value={formData.coment}
                onChange={handleFormChange}
                multiline
                rows={2}
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedBorrow ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this borrow request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb="30px" ml="15px" mr="15px" display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight="bold"
            sx={{ mb: "5px" }}
          >
            Staff Borrows Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderColor: colors.green[400],
              color: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[50],
                borderColor: colors.green[500],
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Borrow Request
          </Button>
        </Box>
      </Box>

      <Divider component="" />

      <Box sx={{ height: 'calc(100vh - 150px)', width: "100%" }}>
        <DataGrid
          rows={borrows || []}
          columns={columns}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          getRowClassName={(params) => {
            switch (params.row.status) {
              case '0': return 'status-pending';
              case '1': return 'status-approved';
              case '2': return 'status-supervisor-approved';
              case '3': return 'status-rejected';
              case '4': return 'status-returned';
              default: return '';
            }
          }}
          sx={{
            border: "none",
            backgroundColor: colors.primary[500],
            ".MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .status-pending .MuiDataGrid-cell": {
              backgroundColor: '#af928257',
              color: colors.orange[200],
              borderBottom: `.2px solid ${colors.orange[400]}`,
            },
            "& .status-approved .MuiDataGrid-cell": {
              backgroundColor: '#6f968459',
              color: colors.newTeal[200],
              borderBottom: `.2px solid ${colors.green[400]}`,
            },
            "& .status-supervisor-approved .MuiDataGrid-cell": {
              backgroundColor: '#60a3d356',
              color: colors.indigo[200],
              borderBottom: `.2px solid ${colors.blue[400]}`,
            },
            "& .status-rejected .MuiDataGrid-cell": {
              backgroundColor: '#cf04223a',
              color: colors.red[100],
              borderBottom: `.2px solid ${colors.red[400]}`,
            },
            "& .status-returned .MuiDataGrid-cell": {
              borderBottom: `.2px solid ${colors.indigo[400]}`,
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: 'transparent',
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Borrow;